import React from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, IconButton } from '@mui/material';
import { Dashboard, People, PersonAdd, Payment, AccountCircle, Logout, Assignment, UploadFile, MenuBook, SupportAgent, Bookmark } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useSchool } from '../contexts/SchoolContext';

const drawerWidth = 180;

const SchoolLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { school, logout } = useSchool();
  const location = useLocation();

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/school/dashboard' },
    { text: 'Students', icon: <People />, path: '/school/students' },
    { text: 'Staff', icon: <PersonAdd />, path: '/school/staff' },
    { text: 'Term Results', icon: <Assignment />, path: '/school/term-results' },
    { text: 'Report Cards', icon: <MenuBook />, path: '/school/report-cards' },
    { text: 'Term History', icon: <Assignment />, path: '/school/term-history' },
    { text: 'Bookmarks', icon: <Bookmark />, path: '/school-work/bookmarks' },
    { text: 'Parent Tickets', icon: <SupportAgent />, path: '/school/parent-tickets' },
    { text: 'Exam Ticket Settings', icon: <UploadFile />, path: '/school/exam-ticket-settings' },
    { text: 'Payments', icon: <Payment />, path: '/school/payments' },
    { text: 'Profile', icon: <AccountCircle />, path: '/school/profile' },
  ];

  const drawer = (
    <Box sx={{ width: drawerWidth, pt: 2 }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path || (item.path === '/school/report-cards' && location.pathname.startsWith('/school/report-cards'))}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': { backgroundColor: 'primary.main' }
              }
            }}
          >
            <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.contrastText' : 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ width: { md: `calc(100% - ${drawerWidth}px)` }, ml: { md: `${drawerWidth}px` } }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" noWrap component="div">
              School Admin
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {school?.name ? `${school.name} Dashboard` : 'School Dashboard'}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: '90px',
              height: 'calc(100% - 90px)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default SchoolLayout;